import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { User, LoginResponse, TenantInfo } from '../types';
import { checkForUpdates } from '../serviceWorkerRegistration';
import {
  PWA_INSTALL_DISMISSED_KEY,
  PWA_INSTALL_ELIGIBLE_KEY,
} from '../constants/pwaInstallPrompt';
import { clearTenantAdminGate } from '../utils/tenantAdminGate';
import { clearTesoreriaFiltrosStorage } from '../utils/tesoreriaFiltrosStorage';
import { clearFechaOperativaSession } from '../utils/fechaOperativaStore';
import { clearPermisosCache } from '../utils/permisoKeys';
import { clearGlobalSucursalCompaniaPrefs } from '../utils/companiaPreferida';
import { devError } from '../utils/devLog';
import {
  AuthBroadcastMessage,
  broadcastAuthMessage,
  clearAllAuthStorage,
  clearTabSession,
  consumeTabTenantQuery,
  getAuthBroadcastChannel,
  getTenantsList,
  persistTabSession,
  readTabSession,
  seedSessionFromLastUsedIfNeeded,
  setTenantsList,
  writeLastUsedFromSession,
} from '../utils/sessionAuthStore';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  /** Tenant actual (multi-tenant). Null si login legacy. */
  tenantId: number | null;
  tenantName: string | null;
  tenantCode: string | null;
  /** Lista de tenants del usuario (para selector "Cambiar empresa"). */
  tenants: TenantInfo[];
  login: (username: string, password: string) => Promise<void>;
  /** Cierra sesión en esta pestaña. Si allTabs=true, cierra también las demás. */
  logout: (options?: { allTabs?: boolean }) => void;
  refreshToken: () => Promise<void>;
  /** Cambia el tenant activo y devuelve un nuevo JWT. */
  switchTenant: (tenantId: number, options?: { reload?: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const applySessionToState = (
  snap: ReturnType<typeof readTabSession>,
  setters: {
    setToken: (t: string | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setUser: (u: User | null) => void;
    setTenantId: (v: number | null) => void;
    setTenantName: (v: string | null) => void;
    setTenantCode: (v: string | null) => void;
  }
) => {
  setters.setToken(snap.token);
  setters.setIsAuthenticated(!!snap.token);
  if (snap.token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${snap.token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
  if (snap.username) {
    setters.setUser({
      username: snap.username,
      displayName: snap.displayName || undefined,
      roles: snap.roles,
    } as User);
  } else {
    setters.setUser(null);
  }
  setters.setTenantId(snap.tenantId);
  setters.setTenantName(snap.tenantName);
  setters.setTenantCode(snap.tenantCode);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const bootSnap = seedSessionFromLastUsedIfNeeded();
  const [token, setToken] = useState<string | null>(() => bootSnap.token);
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!bootSnap.token);
  const [user, setUser] = useState<User | null>(() =>
    bootSnap.username
      ? ({
          username: bootSnap.username,
          displayName: bootSnap.displayName || undefined,
          roles: bootSnap.roles,
        } as User)
      : null
  );
  const [tenantId, setTenantId] = useState<number | null>(() => bootSnap.tenantId);
  const [tenantName, setTenantName] = useState<string | null>(() => bootSnap.tenantName);
  const [tenantCode, setTenantCode] = useState<string | null>(() => bootSnap.tenantCode);
  const [tenants, setTenants] = useState<TenantInfo[]>(() => getTenantsList());
  const tabTenantHandled = useRef(false);
  const tenantIdRef = useRef(tenantId);
  tenantIdRef.current = tenantId;

  const stateSetters = {
    setToken,
    setIsAuthenticated,
    setUser,
    setTenantId,
    setTenantName,
    setTenantCode,
  };

  useEffect(() => {
    const snap = seedSessionFromLastUsedIfNeeded();
    applySessionToState(snap, stateSetters);
    setTenants(getTenantsList());
    if (snap.token) {
      if (snap.username) {
        window.clarity?.('identify', snap.username, undefined, undefined, snap.displayName || snap.username);
      }
      checkForUpdates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const channel = getAuthBroadcastChannel();
    if (!channel) return;

    const onMessage = (event: MessageEvent<AuthBroadcastMessage>) => {
      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;

      if (msg.type === 'logout-all') {
        clearGlobalSucursalCompaniaPrefs();
        clearTabSession();
        applySessionToState(readTabSession(), stateSetters);
        setTenants([]);
        if (window.location.pathname !== '/login') {
          window.location.replace('/login');
        }
        return;
      }

      if (msg.type === 'token-refresh') {
        if (msg.tenantId !== tenantIdRef.current) return;
        persistTabSession({
          token: msg.token,
          refreshToken: msg.refreshToken,
          tenantId: msg.tenantId,
          tenantName: msg.tenantName,
          tenantCode: msg.tenantCode,
          roles: msg.roles,
          username: msg.username,
          displayName: msg.displayName,
          updateLastUsed: true,
        });
        applySessionToState(readTabSession(), stateSetters);
      }
    };

    channel.addEventListener('message', onMessage);
    return () => channel.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username: string, password: string) => {
    try {
      clearTesoreriaFiltrosStorage();
      clearGlobalSucursalCompaniaPrefs();
      const response = await api.post<LoginResponse>('/api/auth/login', { username, password });
      const data = response.data;
      const { token: newToken, refreshToken, username: userUsername, displayName: userDisplayName, roles } = data;

      persistTabSession({
        token: newToken,
        refreshToken,
        username: userUsername,
        displayName: userDisplayName,
        roles: roles || [],
        tenantId: data.tenantId ?? null,
        tenantName: data.tenantName ?? null,
        tenantCode: data.tenantCode ?? null,
        updateLastUsed: true,
      });

      if (data.tenants?.length) {
        setTenantsList(data.tenants);
        setTenants(data.tenants);
      } else {
        setTenantsList([]);
        setTenants([]);
      }

      applySessionToState(readTabSession(), stateSetters);
      sessionStorage.setItem(PWA_INSTALL_ELIGIBLE_KEY, '1');
      window.clarity?.('identify', userUsername, undefined, undefined, userDisplayName || userUsername);
      checkForUpdates();
    } catch (error) {
      devError('Error al iniciar sesión:', error);
      throw error;
    }
  };

  const logout = (options?: { allTabs?: boolean }) => {
    sessionStorage.removeItem(PWA_INSTALL_DISMISSED_KEY);
    sessionStorage.removeItem(PWA_INSTALL_ELIGIBLE_KEY);
    clearTesoreriaFiltrosStorage();
    clearGlobalSucursalCompaniaPrefs();
    clearFechaOperativaSession();
    clearPermisosCache();
    clearTenantAdminGate();
    localStorage.removeItem('grupoCajaChicaSeleccionado');
    localStorage.removeItem('grupoCajaMcp');

    clearTabSession();

    if (options?.allTabs) {
      clearAllAuthStorage();
      broadcastAuthMessage({ type: 'logout-all' });
    }

    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    setTenantId(null);
    setTenantName(null);
    setTenantCode(null);
    if (options?.allTabs) {
      setTenants([]);
    }
    setIsAuthenticated(false);

    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  };

  const refreshToken = async () => {
    try {
      const currentRefresh = readTabSession().refreshToken;
      if (!currentRefresh) {
        logout();
        return;
      }

      const response = await api.post<LoginResponse>('/api/auth/RefreshToken', {
        refreshToken: currentRefresh,
      });
      const data = response.data;
      const newToken = data.token;
      const newRefreshToken = data.refreshToken;

      if (!newToken || !newRefreshToken) {
        throw new Error('Token o refreshToken no recibidos');
      }

      const snap = readTabSession();
      const roles = data.roles ?? snap.roles;
      const resolvedUsername = data.username || snap.username || '';
      const resolvedDisplayName = data.displayName || snap.displayName || undefined;
      const nextTenantId = data.tenantId ?? snap.tenantId;
      const nextTenantName = data.tenantName ?? snap.tenantName;
      const nextTenantCode = data.tenantCode ?? snap.tenantCode;

      persistTabSession({
        token: newToken,
        refreshToken: newRefreshToken,
        roles,
        username: resolvedUsername || null,
        displayName: resolvedDisplayName || null,
        tenantId: nextTenantId,
        tenantName: nextTenantName,
        tenantCode: nextTenantCode,
        updateLastUsed: true,
      });

      applySessionToState(readTabSession(), stateSetters);

      broadcastAuthMessage({
        type: 'token-refresh',
        tenantId: nextTenantId,
        token: newToken,
        refreshToken: newRefreshToken,
        roles,
        username: resolvedUsername || undefined,
        displayName: resolvedDisplayName,
        tenantName: nextTenantName,
        tenantCode: nextTenantCode,
      });
    } catch (error) {
      devError('Error al actualizar el token:', error);
      logout();
    }
  };

  const switchTenant = async (newTenantId: number, options?: { reload?: boolean }) => {
    try {
      const response = await api.post<LoginResponse>('/api/auth/switchtenant', {
        tenantId: newTenantId,
      });
      const data = response.data;
      const roles = data.roles ?? readTabSession().roles;
      const username = data.username || readTabSession().username;
      const displayName = data.displayName || readTabSession().displayName;

      clearGlobalSucursalCompaniaPrefs();

      persistTabSession({
        token: data.token,
        refreshToken: data.refreshToken,
        roles,
        username: username || null,
        displayName: displayName || null,
        tenantId: data.tenantId ?? newTenantId,
        tenantName: data.tenantName ?? null,
        tenantCode: data.tenantCode ?? null,
        updateLastUsed: true,
      });

      applySessionToState(readTabSession(), stateSetters);
      writeLastUsedFromSession();

      if (options?.reload !== false) {
        window.location.reload();
      }
    } catch (error) {
      devError('Error al cambiar tenant:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (tabTenantHandled.current || !isAuthenticated || !token) return;
    const requested = consumeTabTenantQuery();
    if (requested == null) {
      tabTenantHandled.current = true;
      return;
    }
    tabTenantHandled.current = true;
    if (requested === tenantId) return;

    (async () => {
      try {
        await switchTenant(requested, { reload: true });
      } catch (error) {
        devError('Error al aplicar tabTenant:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, tenantId]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        tenantId,
        tenantName,
        tenantCode,
        tenants,
        login,
        logout,
        refreshToken,
        switchTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
